import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../../lib/queryKeys.js';

/** What /sw.js posts to open tabs when a bot's push arrives. */
const botPushMessage = z.object({ type: z.literal('bot-push'), url: z.string() });

/** The thread a push points at ("/chat/<id>"), or null. */
export const threadIdFromPushUrl = (url: string): string | null => {
  const match = /^\/chat\/([^/?#]+)/.exec(new URL(url, window.location.origin).pathname);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

/**
 * Refresh bot threads the moment a bot's push arrives, instead of at the next 60-second check.
 * The open thread refetches only between turns: a refetch mid-turn would replace the reply still
 * streaming into the same cached list.
 */
export const useRefreshOnBotPush = (openThreadId: string, turnSettled: boolean): void => {
  const qc = useQueryClient();
  // Subscribes to the service worker's messages, an external system.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const worker = navigator.serviceWorker;
    const onMessage = (event: MessageEvent<unknown>): void => {
      const push = botPushMessage.safeParse(event.data);
      if (!push.success) return;
      void qc.invalidateQueries({ queryKey: queryKeys.agentBotThreads() });
      if (turnSettled && threadIdFromPushUrl(push.data.url) === openThreadId)
        void qc.invalidateQueries({ queryKey: queryKeys.agentMessages(openThreadId) });
    };
    worker.addEventListener('message', onMessage);
    // Messages wait until a page opts in; adding a listener alone doesn't.
    worker.startMessages();
    return () => worker.removeEventListener('message', onMessage);
  }, [qc, openThreadId, turnSettled]);
};
